import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect, createContext, useContext } from 'react';
import { Copy, Check, Sparkles, ExternalLink, Loader2, KeyRound } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * Context untuk share fetched JWT ke semua config snippet di page ini.
 * Pas user pertama load, TokenFetcher hit /api/auth/playground-token.
 * Kalau session valid → token muncul + auto-inject ke <X-Playground-Token>
 * placeholder di tiap config snippet. Kalau gagal (401, dll) → config tetep
 * pake placeholder `<paste-your-token>` biar manual fallback masih jelas.
 */
const TokenContext = createContext<string | null>(null);
function useFetchedToken() {
  return useContext(TokenContext);
}

export const Route = createFileRoute('/mcp')({
  component: McpSetupPage,
});

const MCP_URL_PROD = 'https://playground.balesotomatis.co.id/mcp';
const MCP_URL_DEV = 'http://localhost:4400/mcp';

function McpSetupPage() {
  const [showDev, setShowDev] = useState(false);
  const [fetchedToken, setFetchedToken] = useState<string | null>(null);
  const mcpUrl = showDev ? MCP_URL_DEV : MCP_URL_PROD;

  return (
    <TokenContext.Provider value={fetchedToken}>
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-12">
      <div className="flex items-center gap-3 mb-5">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-pill border-2 border-border-strong bg-method-patch shadow-brutal-sm">
          <Sparkles size={22} />
        </div>
        <h1 className="font-display font-extrabold tracking-tight text-3xl md:text-4xl">
          MCP Setup
        </h1>
      </div>
      <p className="text-fg-secondary leading-relaxed mb-8 text-base">
        Connect Claude Desktop, Cursor, Cline, atau client MCP lainnya — biar AI bisa call
        BalesOtomatis APIs atas nama lo pake natural language. Playground expose{' '}
        <span className="font-mono font-bold">313 tools</span> (satu per endpoint), scoped
        ke akun lo via JWT.
      </p>

      <div className="inline-flex rounded-pill border-2 border-border-strong bg-elevated p-1 shadow-brutal-sm mb-8">
        <button
          type="button"
          onClick={() => setShowDev(false)}
          className={cn(
            'px-4 h-8 rounded-pill text-2xs font-display font-bold transition-all',
            !showDev ? 'bg-accent border-2 border-border-strong' : 'border-2 border-transparent text-fg-tertiary hover:text-fg'
          )}
        >
          Production
        </button>
        <button
          type="button"
          onClick={() => setShowDev(true)}
          className={cn(
            'px-4 h-8 rounded-pill text-2xs font-display font-bold transition-all',
            showDev ? 'bg-accent border-2 border-border-strong' : 'border-2 border-transparent text-fg-tertiary hover:text-fg'
          )}
        >
          Local dev
        </button>
      </div>

      <Section number={1} title="Get your playground token">
        <p className="text-fg-secondary leading-relaxed mb-3">
          Log in at <a className="text-accent hover:underline" href="https://app.balesotomatis.co.id">app.balesotomatis.id</a>
          {' '}— this sets the <span className="font-mono text-fg">bo_session</span> cookie. Then click below to copy
          your token (it's the same JWT used by this playground).
        </p>
        <TokenFetcher onToken={setFetchedToken} />
      </Section>

      <Section number={2} title="Claude Desktop">
        <p className="text-fg-secondary leading-relaxed mb-3">
          Add this to <span className="font-mono text-fg">~/Library/Application Support/Claude/claude_desktop_config.json</span>
          {' '}(macOS) or <span className="font-mono text-fg">%APPDATA%\Claude\claude_desktop_config.json</span> (Windows):
        </p>
        <ConfigSnippet kind="claude" url={mcpUrl} />
        <p className="text-2xs text-fg-tertiary mt-2">Restart Claude Desktop after editing.</p>
      </Section>

      <Section number={3} title="Cursor">
        <p className="text-fg-secondary leading-relaxed mb-3">
          Open <span className="font-mono text-fg">Cmd+,</span> → search "MCP" → add a new server:
        </p>
        <ConfigSnippet kind="cursor" url={mcpUrl} />
      </Section>

      <Section number={4} title="Cline / Continue.dev">
        <p className="text-fg-secondary leading-relaxed mb-3">
          Add to <span className="font-mono text-fg">~/.continue/config.json</span> under{' '}
          <span className="font-mono text-fg">experimental.modelContextProtocolServers</span>:
        </p>
        <ConfigSnippet kind="cline" url={mcpUrl} />
      </Section>

      <Section number={5} title="Try it">
        <div className="rounded-brutal border-[2.5px] border-border-strong bg-method-get/50 shadow-brutal-sm p-5 space-y-2">
          <p className="font-display font-bold text-sm mb-2">Contoh prompt:</p>
          <ul className="space-y-1.5 text-sm text-fg-secondary">
            <li>• "List my active chats from BalesOtomatis"</li>
            <li>• "Send a WhatsApp message to +6281234567890 saying 'Hello'"</li>
            <li>• "Show today's AI suggestion analytics"</li>
            <li>• "Search contacts for 'jakarta'"</li>
          </ul>
        </div>
      </Section>

      <div className="mt-12 p-5 rounded-brutal border-[2.5px] border-border-strong bg-method-put shadow-brutal text-sm">
        <p className="font-display font-extrabold mb-1.5">⚠ Security note</p>
        <p className="text-fg-secondary leading-relaxed">
          Token di config grants full API access scoped to akun lo. Jangan di-commit.
          Token expire after 2 hours by default — re-copy kalau perlu. Phase 3 nanti
          ganti dengan proper OAuth flow.
        </p>
      </div>
    </div>
    </TokenContext.Provider>
  );
}

function Section({
  number,
  title,
  children,
}: {
  number: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-10">
      <h2 className="flex items-center gap-3 mb-4">
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-pill border-2 border-border-strong bg-accent shadow-brutal-sm font-display font-extrabold text-base">
          {number}
        </span>
        <span className="font-display font-extrabold text-xl">{title}</span>
      </h2>
      {children}
    </section>
  );
}

/**
 * Auto-fetch playground JWT pas page load. Kalau user udah login → token
 * langsung muncul, gak perlu paste manual. Token juga di-broadcast via
 * onToken callback supaya CodeBlock di Section 2/3/4 bisa auto-inject ke
 * config snippet (no `<paste-token-here>` placeholder lagi).
 */
function TokenFetcher({ onToken }: { onToken: (token: string | null) => void }) {
  const [token, setToken] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch('/api/auth/playground-token', { credentials: 'include' });
        if (cancelled) return;
        if (!res.ok) {
          if (res.status === 401) {
            throw new Error('Belum login — silakan login dulu di app.balesotomatis.id');
          }
          throw new Error(`HTTP ${res.status}`);
        }
        const data = await res.json();
        if (cancelled) return;
        if (!data.success || !data.token) {
          throw new Error(data.error || 'No token returned');
        }
        setToken(data.token);
        setExpiresAt(data.expires_at ?? null);
        onToken(data.token);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copy = async () => {
    if (!token) return;
    await navigator.clipboard.writeText(token);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const expiresInMin = expiresAt
    ? Math.max(0, Math.floor((expiresAt - Date.now()) / 60_000))
    : null;

  return (
    <div className="rounded-brutal border-[2.5px] border-border-strong bg-elevated shadow-brutal-sm p-5">
      {loading && (
        <div className="flex items-center gap-2 text-fg-secondary text-sm">
          <Loader2 size={14} className="animate-spin" />
          <span>Loading your session token...</span>
        </div>
      )}

      {error && (
        <div className="space-y-3">
          <div className="flex items-start gap-2 text-sm">
            <KeyRound size={16} className="shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
          <a
            href="https://app.balesotomatis.co.id"
            className="inline-flex items-center gap-1.5 px-4 h-10 rounded-pill border-2 border-border-strong bg-accent shadow-brutal-sm hover:shadow-brutal hover:-translate-y-0.5 active:translate-y-0.5 active:shadow-brutal-none transition-all text-sm font-display font-bold"
          >
            Login di app.balesotomatis.id
            <ExternalLink size={13} />
          </a>
        </div>
      )}

      {token && !loading && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-2xs font-display font-bold uppercase tracking-wider">
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-pill border-2 border-border-strong bg-method-get">
              <Check size={11} />
            </span>
            Token siap dipakai
            {expiresInMin !== null && (
              <span className="font-mono font-normal normal-case tracking-normal text-fg-tertiary">
                · expire dalam {expiresInMin} menit
              </span>
            )}
          </div>
          <div className="rounded-brutal-sm border-2 border-border-strong bg-base p-3 font-mono text-2xs text-fg-secondary break-all leading-relaxed">
            {token.slice(0, 32)}…{token.slice(-12)}
          </div>
          <button
            type="button"
            onClick={copy}
            className="inline-flex items-center gap-1.5 px-3 h-9 rounded-pill border-2 border-border-strong bg-elevated shadow-brutal-sm hover:shadow-brutal hover:-translate-y-0.5 active:translate-y-0.5 active:shadow-brutal-none transition-all text-2xs font-display font-bold"
          >
            {copied ? <Check size={12} /> : <Copy size={12} />}
            {copied ? 'Copied!' : 'Copy full token'}
          </button>
          <p className="text-2xs text-fg-tertiary leading-relaxed">
            Token udah auto-inject ke 3 config snippet di bawah — tinggal copy aja yang sesuai
            sama AI client lo.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Wrapper untuk CodeBlock yang tau jenis config + auto-inject token dari
 * TokenContext. Tampilkan badge "auto-filled" kalau token udah ter-fetch.
 */
function ConfigSnippet({ kind, url }: { kind: 'claude' | 'cursor' | 'cline'; url: string }) {
  const token = useFetchedToken();
  const code =
    kind === 'claude'
      ? claudeDesktopConfig(url, token)
      : kind === 'cursor'
      ? cursorConfig(url, token)
      : clineConfig(url, token);
  return (
    <div className="relative">
      {token && (
        <div className="absolute -top-3 left-3 z-10 inline-flex items-center gap-1 px-2 h-5 rounded-pill border-2 border-border-strong bg-method-get text-2xs font-display font-bold shadow-brutal-sm">
          <Check size={9} />
          Token auto-filled
        </div>
      )}
      <CodeBlock language="json" code={code} />
    </div>
  );
}

function CodeBlock({ language, code }: { language: string; code: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="relative rounded-brutal border-[2.5px] border-border-strong bg-base shadow-brutal-sm overflow-hidden">
      <div className="absolute top-2.5 right-2.5 flex items-center gap-2">
        <span className="text-2xs font-mono font-bold text-fg-tertiary">{language}</span>
        <button
          type="button"
          onClick={copy}
          className="w-7 h-7 rounded-pill border-2 border-border-strong bg-elevated shadow-brutal-sm hover:shadow-brutal hover:-translate-y-0.5 active:translate-y-0.5 active:shadow-brutal-none transition-all flex items-center justify-center"
          title="Copy"
        >
          {copied ? <Check size={12} /> : <Copy size={12} />}
        </button>
      </div>
      <pre className="px-4 py-3.5 font-mono text-2xs text-fg-secondary leading-relaxed overflow-x-auto">
        <code>{code}</code>
      </pre>
    </div>
  );
}

/**
 * Config generators. Token = fetched JWT (auto-injected dari TokenContext)
 * atau placeholder `<paste-your-token>` kalau user belum login.
 */
function claudeDesktopConfig(url: string, token: string | null): string {
  return JSON.stringify(
    {
      mcpServers: {
        balesotomatis: {
          url,
          headers: { 'X-Playground-Token': token || '<paste-your-token-here>' },
        },
      },
    },
    null,
    2
  );
}

function cursorConfig(url: string, token: string | null): string {
  return JSON.stringify(
    {
      'mcp.servers': {
        balesotomatis: {
          url,
          transport: 'streamable-http',
          headers: { 'X-Playground-Token': token || '<paste-your-token-here>' },
        },
      },
    },
    null,
    2
  );
}

function clineConfig(url: string, token: string | null): string {
  return JSON.stringify(
    {
      experimental: {
        modelContextProtocolServers: [
          {
            transport: {
              type: 'streamable-http',
              url,
              headers: { 'X-Playground-Token': token || '<paste-your-token-here>' },
            },
          },
        ],
      },
    },
    null,
    2
  );
}
